import React from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Paper
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

export interface FilterOption {
  value: string;
  label: string;
}

export interface FilterConfig {
  id: string;
  label: string;
  options: FilterOption[];
  value: string;
}

interface FilterBarProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  filters?: FilterConfig[];
  onFilterChange?: (filterId: string, value: string) => void;
  compact?: boolean;
}

/**
 * A reusable filter bar component with search and dropdown filters
 */
const FilterBar: React.FC<FilterBarProps> = ({
  searchTerm,
  onSearchChange,
  searchPlaceholder = 'Search...',
  filters = [],
  onFilterChange,
  compact = true,
}) => {
  const handleFilterChange = (filterId: string) => (event: SelectChangeEvent) => {
    onFilterChange?.(filterId, event.target.value);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: compact ? 1 : 2,
        mb: 2,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
          width: '100%',
        }}
      >
        <TextField
          label={compact ? undefined : "Search"}
          placeholder={searchPlaceholder}
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ 
            flexGrow: 1,
            minWidth: { xs: '100%', sm: '200px' }
          }}
        />

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          {filters.map((filter) => (
            <FormControl
              key={filter.id}
              size="small"
              sx={{ minWidth: { xs: '100%', sm: '150px' } }}
            >
              <InputLabel>{filter.label}</InputLabel>
              <Select
                value={filter.value}
                onChange={handleFilterChange(filter.id)}
                label={filter.label}
              >
                {filter.options.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ))}
        </Box>
      </Box>
    </Paper>
  );
};

export default FilterBar;
